import { useEffect, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Landmark, Plus } from 'lucide-react'
import { listAccounts } from '@/api/accounts'
import { getLoanDetail, type LoanDetail } from '@/api/loans'
import { listPersonalLoans } from '@/api/personalLoans'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { useReadOnlyKnown } from '@/hooks/useReadOnlyKnown'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { TabButton } from '@/components/ui/tab-button'
import { AddLoanModal, LoanPaymentModal } from '@/components/loans/LoanModals'
import { LoanCard } from '@/components/loans/LoanCard'
import { LoansHero } from '@/components/loans/LoansHero'
import { emiRecordedInMonth } from '@/components/loans/loanStats'
import { PersonalLoansTab } from '@/components/personalLoans/PersonalLoansSection'
import { PersonalLoanFormModal } from '@/components/personalLoans/PersonalLoanFormModal'
import { getDefaultAccountIcon } from '@/lib/accountIconOptions'
import { PERSONAL_LOANS_ENABLED } from '@/lib/featureFlags'
import { cn } from '@/lib/utils'

type Tab = 'bank' | 'people'

export default function LoansPage() {
  const isReadOnly = useIsReadOnly()
  const readOnlyKnown = useReadOnlyKnown()
  const { formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: Tab =
    PERSONAL_LOANS_ENABLED && searchParams.get('tab') === 'people' ? 'people' : 'bank'

  // ?add=1 (an Analytics nudge) opens the bank-loan form ready to fill; dropping the param keeps a refresh from
  // reopening it. Read-only is only known once its queries load, so the modal is gated at render too.
  const [showCreate, setShowCreate] = useState(
    () => searchParams.get('add') === '1' && tab === 'bank' && !isReadOnly
  )
  // Only a form a link opened waits for read-only to be known: a click on Add comes after the page loads.
  const [openedByLink] = useState(showCreate)
  useEffect(() => {
    if (searchParams.get('add') === null) return
    const next = new URLSearchParams(searchParams)
    next.delete('add')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  const [creatingPersonal, setCreatingPersonal] = useState(false)
  const [payingEmi, setPayingEmi] = useState<LoanDetail | null>(null)
  const [showRepaid, setShowRepaid] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts(),
  })
  const { data: personalLoans } = useQuery({
    queryKey: ['personal-loans', 'all'],
    queryFn: () => listPersonalLoans(),
    enabled: PERSONAL_LOANS_ENABLED,
  })

  const loans = data?.accounts.filter((a) => a.type === 'LOAN') ?? []
  const details = useQueries({
    queries: loans.map((l) => ({
      queryKey: ['loan', l.id],
      queryFn: () => getLoanDetail(l.id),
    })),
  })
  const detailById = new Map(details.flatMap((q) => (q.data ? [[q.data.id, q.data] as const] : [])))
  const accountName = (id: string | null) => data?.accounts.find((a) => a.id === id)?.name

  const today = new Date()
  const monthShort = today.toLocaleDateString('en-IN', { month: 'short' })
  const loaded = loans.flatMap((l) => {
    const d = detailById.get(l.id)
    return d ? [{ account: l, detail: d }] : []
  })
  const active = loaded.filter((l) => l.detail.outstanding > 0)
  const repaid = loaded.filter((l) => l.detail.outstanding <= 0)
  const withEmi = active.filter((l) => l.detail.emiAmount != null)
  const emiRecorded = withEmi.filter((l) => emiRecordedInMonth(l.detail.entries, today)).length
  const totalOutstanding = loans.reduce((sum, l) => sum + Math.max(l.balance, 0), 0)
  const owing = loans.filter((l) => l.balance > 0).length
  const sumOf = (pick: (d: LoanDetail) => number) =>
    loaded.reduce((sum, l) => sum + pick(l.detail), 0)
  const principalRepaid = sumOf((d) => d.principalRepaid)
  const interestPaid = sumOf((d) => d.interestPaid)

  function selectTab(next: Tab) {
    setSearchParams(next === 'people' ? { tab: 'people' } : {}, { replace: true })
  }

  return (
    <div className="flex flex-col gap-3 p-4 md:gap-5 md:p-10">
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-xl font-semibold text-[var(--fl-heading)] md:text-[40px] md:font-bold md:leading-tight">
          Loans
        </h2>
        {!isReadOnly && (
          // The floating ＋ adds a transaction, so on a phone this stays a quiet pill rather than a second loud button.
          <Button
            aria-label={tab === 'bank' ? 'Add loan' : 'Give or take a loan'}
            variant={wide ? 'default' : 'outline'}
            onClick={() => (tab === 'bank' ? setShowCreate(true) : setCreatingPersonal(true))}
            className={cn(
              'font-semibold active:scale-95',
              wide
                ? 'h-auto px-4 py-2.5'
                : 'h-9 gap-1 rounded-full border-[var(--fl-primary)] px-3 text-[13px] text-[var(--fl-primary)]',
              tab === 'bank' && data && loans.length === 0 && 'motion-safe:animate-attention-pulse'
            )}
          >
            <Plus size={wide ? 16 : 14} />{' '}
            {tab === 'bank'
              ? wide
                ? 'Add loan'
                : 'Loan'
              : wide
                ? 'Give or take a loan'
                : 'Lend or borrow'}
          </Button>
        )}
      </header>

      {PERSONAL_LOANS_ENABLED && (
        <div
          role="tablist"
          aria-label="Loan kind"
          className="flex gap-1 border-b border-[var(--fl-border)]"
        >
          <TabButton
            active={tab === 'bank'}
            count={data ? loans.length : undefined}
            onClick={() => selectTab('bank')}
          >
            Bank loans
          </TabButton>
          <TabButton
            active={tab === 'people'}
            count={personalLoans?.length}
            onClick={() => selectTab('people')}
          >
            With people
          </TabButton>
        </div>
      )}

      {tab === 'people' ? (
        <PersonalLoansTab />
      ) : (
        <>
          {isLoading && <p className="text-sm text-[var(--fl-text-2)]">Loading…</p>}
          {isError && <p className="text-sm text-[var(--fl-error)]">Failed to load loans.</p>}

          {data && loans.length === 0 && (
            <EmptyState
              icon={Landmark}
              title="Track your first loan"
              description="A home, car, gold or personal loan — enter what you still owe, then log each EMI as you pay it."
            />
          )}

          {loans.length > 0 && (
            <LoansHero
              ariaLabel="Bank loans summary"
              label={wide ? `You owe on loans · ${owing} active` : 'You owe on loans'}
              amount={totalOutstanding}
              split={{ good: principalRepaid, bad: interestPaid }}
              note={
                principalRepaid + interestPaid > 0
                  ? `Paid ${formatWhole(principalRepaid + interestPaid)} since you started tracking`
                  : undefined
              }
              items={[
                {
                  label: 'EMIs per month',
                  amount: withEmi.reduce((sum, l) => sum + (l.detail.emiAmount ?? 0), 0),
                  sub:
                    withEmi.length > 0
                      ? `${emiRecorded === withEmi.length ? '✓ ' : ''}${emiRecorded} of ${withEmi.length} recorded for ${monthShort}`
                      : 'no EMIs set',
                },
                { label: 'Principal repaid', amount: principalRepaid, tone: 'income' },
                { label: 'Interest paid', amount: interestPaid, tone: 'expense' },
              ]}
            />
          )}

          {active.length > 0 && (
            <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2 2xl:grid-cols-3">
              {active.map(({ account, detail }) => (
                <LoanCard
                  key={detail.id}
                  loan={detail}
                  icon={account.icon || getDefaultAccountIcon('LOAN', account.subType)}
                  accountName={accountName}
                  today={today}
                  canPay={!isReadOnly && !detail.archivedAt}
                  onPayEmi={() => setPayingEmi(detail)}
                />
              ))}
            </div>
          )}

          {repaid.length > 0 && (
            <section className="border-t border-[var(--fl-divider)] pt-1">
              <button
                type="button"
                onClick={() => setShowRepaid((s) => !s)}
                aria-expanded={showRepaid}
                className="flex w-full items-center gap-2 py-2.5 text-left text-[13.5px] text-[var(--fl-text-2)] hover:text-[var(--fl-heading)]"
              >
                {showRepaid ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Repaid loans ({repaid.length})
              </button>
              {showRepaid && (
                <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                  {repaid.map(({ account, detail }) => (
                    <LoanCard
                      key={detail.id}
                      loan={detail}
                      icon={account.icon || getDefaultAccountIcon('LOAN', account.subType)}
                      accountName={accountName}
                      today={today}
                      canPay={false}
                      onPayEmi={() => {}}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {showCreate && !isReadOnly && (readOnlyKnown || !openedByLink) && (
        <AddLoanModal onClose={() => setShowCreate(false)} />
      )}
      {creatingPersonal && <PersonalLoanFormModal onClose={() => setCreatingPersonal(false)} />}
      {payingEmi && (
        <LoanPaymentModal loan={payingEmi} type="EMI" onClose={() => setPayingEmi(null)} />
      )}
    </div>
  )
}
